import { ChangeDetectionStrategy, Component, computed, DestroyRef, OnInit, TemplateRef, viewChild } from '@angular/core';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { UiKitModule } from '../../../../shared/ui-kit/ui-kit.module';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { PipesModule } from '../../../../pipes/pipes.module';
import { OrgVariableDialogComponent } from './dialogs/org-variable-dialog/org-variable-dialog.component';
import { ConfirmDialogModel, UiButtonComponent } from '@ui';
import * as SharedActions from '@states/shared/shared.actions';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ofType } from '@ngrx/effects';
import { SharedEffects } from '@effects/shared.effects';
import { OrgVariablesSelectors } from '@states/org-variables/org-variables.selector-type';
import { OrgVariablesActions } from '@states/org-variables/org-variables.action-types';
import { OrgVariablesModel } from '@models/org-variables.model';
import { RouteModalService } from '@ui';
import { openDialogByParams } from '@consts/open-dialog-by-params.consts';
import { syncQueryParam } from '../../../../helpers/sync-query-param.helper';
import { PreloaderColor } from '@enums/shared.enum';
import { features } from '@consts/text.const';
import { UiSearchComponent } from '../../../../shared/ui-kit/ui-search/ui-search.component';
import { UiNoDataComponent } from '@ui';
import { UiPreloaderComponent } from '../../../../shared/ui-kit/ui-preloader/ui-preloader.component';
import { UiDatasetTableComponent } from '@ui/lib/ui-kit/ui-dataset-table';
import { createVariablesTableConfig, VariableCellContext } from './variables-table.util';
import { MenuActions } from "@states/menu/menu.action-types";
import { MenuKey } from "@enums/menu.enum";
import { ViewStructureModalComponent } from '../../../../shared/modals/view-structure-modal/view-structure-modal.component';

type ViewState = 'LOADING' | 'HAS_DATA' | 'NO_RESULTS' | 'INITIAL_EMPTY';

@Component({
  selector: 'app-org-variables',
  imports: [
    TranslatePipe,
    UiKitModule,
    PipesModule,
    UiSearchComponent,
    UiNoDataComponent,
    UiPreloaderComponent,
    UiButtonComponent,
    UiDatasetTableComponent,
  ],
  templateUrl: './variables.component.html',
  styleUrl: './variables.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class VariablesComponent implements OnInit {
  public isLoading = this.store$.selectSignal(OrgVariablesSelectors.selectIsLoading);
  public alertVariables = this.store$.selectSignal(OrgVariablesSelectors.selectVariables);

  protected searchQuery = syncQueryParam<string>('search');
  protected readonly valueCellTemplate = viewChild<TemplateRef<VariableCellContext>>('valueCell');
  protected readonly typeCellTemplate = viewChild<TemplateRef<VariableCellContext>>('typeCell');

  protected readonly noData = features.variables.noData;
  protected readonly noResults = features.variables.noResults;
  protected readonly loaderColor = PreloaderColor;

  protected formatVariableType(type: string): string {
    if (!type) return '';
    return type.charAt(0).toUpperCase() + type.slice(1);
  }

  public readonly tableConfig = computed(() => {
    const valueTemplate = this.valueCellTemplate();
    const typeTemplate = this.typeCellTemplate();
    if (!valueTemplate || !typeTemplate) return null;

    return createVariablesTableConfig({
      valueCellTemplate: valueTemplate,
      typeCellTemplate: typeTemplate,
    });
  });

  public readonly vm$ = computed(() => {
    const isLoading = this.isLoading();
    const variables = this.alertVariables();
    const query = this.searchQuery();

    const filteredVariables = !query
      ? variables
      : variables.filter((p) => p.name.toLowerCase().includes(query.toLowerCase()));

    let viewState: ViewState;
    if (isLoading && !variables.length) {
      viewState = 'LOADING';
    } else if (variables.length > 0 && query && !filteredVariables.length) {
      viewState = 'NO_RESULTS';
    } else if (variables.length > 0) {
      viewState = 'HAS_DATA';
    } else {
      viewState = 'INITIAL_EMPTY';
    }

    return {
      viewState,
      filteredVariables,
      searchQuery: query,
    };
  });

  constructor(
    private dialog: MatDialog,
    private store$: Store,
    private translateService: TranslateService,
    private destroyRef: DestroyRef,
    private sharedEffects: SharedEffects,
    private modalService: RouteModalService,
  ) { }

  public ngOnInit() {
    this.store$.dispatch(OrgVariablesActions.getOrgVariables());
    this.store$.dispatch(MenuActions.setActiveLevel2Menu({ activeLevel2: MenuKey.variables }));

    this.sharedEffects.confirmation$
      .pipe(takeUntilDestroyed(this.destroyRef), ofType(SharedActions.showConfirmModalResultConfirm))
      .subscribe((result) => {
        const name = result?.params['variableName'];
        if (name) {
          this.store$.dispatch(OrgVariablesActions.removeVariable({ name }));
        }
      });

    this.modalService.initModalListeners(
      [
        {
          queryParam: openDialogByParams.createVariable,
          open: () => this.openCreateVariableDialog(),
        },
      ],
      this.destroyRef,
    );
  }

  public viewJson(json): void {
    this.dialog.open(ViewStructureModalComponent, {
      panelClass: 'thumbnail-dialog-wrapper',
      width: '80vw',
      height: '600px',
      data: json,
    });
  }

  public createNewVariable() {
    this.modalService.openModal(openDialogByParams.createVariable);
  }

  public editVariable(variable: OrgVariablesModel.Variable): void {
    this.dialog
      .open(OrgVariableDialogComponent, {
        width: '400px',
        panelClass: 'modal-no-padding',
        data: variable,
      })
      .afterClosed()
      .subscribe((formValue) => {
        if (formValue) {
          SharedActions.setIsLoading({ isLoading: false });
          this.store$.dispatch(OrgVariablesActions.editVariable({ variable: formValue }));
        }
      });
  }

  public onRowAction(event: {
    actionId: string;
    row: OrgVariablesModel.Variable;
  }): void {
    if (event.actionId === 'edit') {
      this.editVariable(event.row);
    } else if (event.actionId === 'delete') {
      this.remove(event.row.name);
    }
  }

  public remove(name: string): void {
    this.store$.dispatch(
      SharedActions.showConfirmModal({
        options: {
          type: ConfirmDialogModel.ConfirmDialogType.CONFIRM,
          msg: this.translateService.instant('DELETE_VARIABLE_MODAL.MSG', { name }),
          title: this.translateService.instant('DELETE_VARIABLE_MODAL.TITLE'),
          confirm: this.translateService.instant('CONFIRM_MODALS.COMMON_YES'),
          disableClose: true,
          params: {
            variableName: name,
          },
        },
      }),
    );
  }

  private openCreateVariableDialog(): MatDialogRef<OrgVariableDialogComponent> {
    const dialogRef = this.dialog.open(OrgVariableDialogComponent, {
      width: '400px',
      panelClass: 'modal-no-padding',
    });

    dialogRef.afterClosed().subscribe((formValue) => {
      if (formValue) {
        SharedActions.setIsLoading({ isLoading: false });
        this.store$.dispatch(OrgVariablesActions.addVariable({ variable: formValue }));
      }
    });

    return dialogRef;
  }
}
